import * as _ from "lodash";
import {
    IEntity,
    IEntityCreateOptions,
    IEntityHeader,
    IEntityManager,
    IEntityManagerFindOptions, IEntityManagerQueryOptions
} from "../PA.MD.Core/PA.MD.IF.Entity";
import {PASearch} from "../PA.Core/PASearch";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {EntityTypeUtils} from "./EntityTypeUtils";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {
    esWB_DistribChannel,
    esWB_Division,
    esWB_Finished_Product,
    esWB_Finished_Product_Relationship, esWB_Finished_Product_SalesArea,
    esWB_Finished_Product_Tag_Relationship, esWB_Product_Attribute,
    esWB_Product_BOM,
    esWB_Product_BOM_Component,
    esWB_Product_Group,
    esWB_Product_Group_External_Relationship,
    esWB_Product_Group_Relationship, esWB_Product_Group_SalesArea,
    esWB_Product_Group_Tag_Relationship,
    esWB_Product_Type,
    esWB_SalesArea,
    esWB_SalesOrg
} from "../PA.Core/PAdbSchema";
import {WB_FinishedProductRow, WB_ProductGroupRow, WB_ProductTypeRow} from "../PA.Core/PAdbSchemaRows";
import {ProductEntity} from "./ProductEntity";
import {ProductGroupBO} from "../PA.MD.BusinessObjects/ProductGroupBO";

let stringFields = [
    'ENTITY_ID', 'ENTITY_NAME', 'ID_UI',
    'KEY_SET', 'MANUAL_IND', 'MDSET_ID', 'MDSET_NAME', 'NAME',
    'SALESAREA_ID', 'SALESAREA_NAME', 'SALESORG_ID', 'SALESORG_NAME', 'SOURCE_ID','ACTIVE_IND']

let numberFields = ['WB_ENTITY_ID', 'WB_ID', 'WB_MDSET_ID', 'WB_SALESAREA_ID', 'WB_SALESORG_ID', 'WB_COMPANY_ID']
const vWB_Product_Entity_View_Fields: { [id: string]: { type: string } } = PASearch.createSimpleSchema(stringFields, numberFields)


export class ProductEntityManager
    implements IEntityManager {
    constructor() {
    }

    async findEntitiesByType(wbEntityId: number, arrWbId: number[], options?: IEntityManagerFindOptions): Promise<IEntity[]> {

        let rv: IEntity[] = []
        let dbContext: PAdbContext = options?.dbContext
        if (_.isEmpty(dbContext))
            dbContext = new PAdbContext()
        let et = await EntityTypeUtils.getEntityTypeRow(wbEntityId)
        if (_.isEmpty(et))
            return []

        let wbPrdMRow = <WB_ProductTypeRow>await dbContext.find(esWB_Product_Type, et.cif.META_WB_ID)

        // Use record resolver to get data, create simple Entity for all Customers
        const prod_options: CreateOptions = {
            standardConditions: {
                [esWB_Product_Attribute]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Finished_Product_SalesArea]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Product_Group_SalesArea]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_SalesArea]: [esWB_SalesOrg, esWB_Division, esWB_DistribChannel]
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_Finished_Product]: [esWB_Finished_Product_Relationship,
                    esWB_Product_BOM, esWB_Product_BOM_Component,
                    esWB_Finished_Product_Relationship, esWB_Finished_Product_Tag_Relationship],
                [esWB_Product_Group]: [esWB_Product_Group_Relationship, esWB_Finished_Product_Relationship,
                    esWB_Product_Group_Tag_Relationship, esWB_Product_Group_External_Relationship]
            }
        }

        let rr = new ESRecordResolver(dbContext, prod_options)

        if (wbPrdMRow.cif.PRDM_TYPE == 'Product') {
            let rows: WB_FinishedProductRow[] = <any[]>await rr.findAllWithRelationships(esWB_Finished_Product, arrWbId, {})
            // Instantiate Product Entity
            for (let aRow of rows) {
                let ce = new ProductEntity({productRow: aRow})
                await ce.OnLoadComplete()
                rv.push(ce)
            }

        } else if (wbPrdMRow.cif.PRDM_TYPE == 'ProductGroup') {
            let rows: WB_ProductGroupRow[] = <any[]>await rr.findAllWithRelationships(esWB_Product_Group, arrWbId, {})
            // Instantiate Product Group Entity
            for (let aRow of rows) {
                let ce = new ProductEntity({productGroupRow: aRow})
                await ce.OnLoadComplete()
                rv.push(ce)
            }
        }

        return rv
    }

    async findEntityByType(wbEntityId: number, wbId: number, options?: IEntityManagerFindOptions): Promise<IEntity> {
        let arrEntities = await this.findEntitiesByType(wbEntityId, [wbId], options)
        if (arrEntities.length)
            return arrEntities[0]
        return undefined
    }

    async findEntityHeader(wbEntityId: number, wbId: number, options?: IEntityManagerFindOptions): Promise<IEntityHeader> {

        let dbContext: PAdbContext = options?.dbContext
        if (_.isEmpty(dbContext))
            dbContext = new PAdbContext()
        let et = await EntityTypeUtils.getEntityTypeRow(wbEntityId)
        if (_.isEmpty(et))
            return
        if (!(wbId > 0))
            return undefined

        switch (et.cif.SET_TYPE) {
            case "Product":
                let arrPrdRow = <WB_FinishedProductRow[]>await dbContext.findUsingCache(esWB_Finished_Product, [wbId])

                if (_.isEmpty(arrPrdRow))
                    return undefined

                let prdRow = _.first(arrPrdRow)
                return {
                    dbContext: dbContext,
                    wbMasterDataSetId: prdRow.cif.WB_MDSET_ID,
                    mapperName: esWB_Finished_Product,
                    wb_id: wbId,
                    id_ui: prdRow.cif.PRODUCT_ID,
                    key_set: prdRow.cif.PRODUCT_SET_ID,
                    name: prdRow.cif.PRODUCT_NAME
                }
            case "ProductGroup":
                let arrPGRow = <WB_ProductGroupRow[]>await dbContext.findUsingCache(esWB_Product_Group, [wbId])

                if (_.isEmpty(arrPGRow))
                    return undefined

                let pgRow = _.first(arrPGRow)
                return {
                    dbContext: dbContext,
                    wbMasterDataSetId: pgRow.cif.WB_MDSET_ID,
                    mapperName: esWB_Product_Group,
                    wb_id: wbId,
                    id_ui: pgRow.cif.PRODUCT_GROUP_ID,
                    key_set: pgRow.cif.PRODUCT_GROUP_SET_ID,
                    name: pgRow.cif.PRODUCT_GROUP_NAME
                }
        }

        return undefined

    }

    async queryEntitiesByType(wbEntityId: number, condition: { [p: string]: any }, options: IEntityManagerQueryOptions): Promise<number[]> {

        let cond = condition || {}
        if ('where' in condition)
            cond = condition.where || {}

        let pCond = {where: {}}
        if (!_.isEmpty(cond))
            pCond = {where: cond}

        // @ts-ignore
        pCond.where.WB_ENTITY_ID = wbEntityId
        if(options?.bActiveOnly)
            pCond.where.ACTIVE_IND = '1'

        let results = await PASearch.find('vUI_Product_Entity_Search',
            pCond,
            vWB_Product_Entity_View_Fields,
            [],
            0,
            ['WB_ID'])

        return _.map(results, (a) => +a.WB_ID)
    }

    async queryEntitiesByTypeEnterpriseType(wbEntityId: number, wbEntityId_Enterprise: number, condition: {
        [p: string]: any
    }, options: IEntityManagerQueryOptions): Promise<{ wbId: number; wbId_Ent: number }[]> {
        let cond = condition || {}
        if ('where' in condition)
            cond = condition.where || {}

        let pCond = {where: {}}
        if (!_.isEmpty(cond))
            pCond = {where: cond}

        // @ts-ignore
        pCond.where.WB_ENTITY_ID = wbEntityId
        if(options?.bActiveOnly)
            pCond.where.ACTIVE_IND = '1'

        let sEntWbIdFieldName = ''
        let wbEntityRow_Ent = await EntityTypeUtils.getEntityTypeRow(wbEntityId_Enterprise)
        if (_.isEmpty(wbEntityRow_Ent))
            return []

        switch (wbEntityRow_Ent.cif.ENTITY_ID) {
            case "Company":
                sEntWbIdFieldName = 'WB_COMPANY_ID'
                break
            case "SalesOrg":
                sEntWbIdFieldName = 'WB_SALESORG_ID'
                break
            case "SalesArea":
                sEntWbIdFieldName = 'WB_SALESAREA_ID'
                break
        }

        if (_.isEmpty(sEntWbIdFieldName))
            return []


        let results = await PASearch.find('vUI_Product_Entity_Search',
            pCond,
            vWB_Product_Entity_View_Fields,
            [],
            0,
            ['WB_ID', sEntWbIdFieldName])

        return _.map(results, (a) => {
            return {wbId: +a.WB_ID, wbId_Ent: +a[sEntWbIdFieldName]}
        })
    }

    async checkEntityExists(wbEntityId: number, key_set: string, id_ui: string): Promise<{
        wbId: number;
        wbEntityIds: number[];
        entityIds: string[];
        dupError?: boolean
    }> {
        //
        let e = await EntityTypeUtils.getEntityTypeRow(wbEntityId)

        //--
        let results: any[] = await PASearch.find('vUI_Product_Entity_Search',
            {
                where: {
                    WB_MDSET_ID: e.cif.WB_MDSET_ID,
                    ID_UI: id_ui,
                    KEY_SET: key_set
                }
            },
            vWB_Product_Entity_View_Fields,
            [],
            0,
            ['WB_ID', 'WB_ENTITY_ID', 'ENTITY_ID'])

        if (results.length) {
            let wbId = +results[0].WB_ID
            let rv: { wbId: number, wbEntityIds: number[], entityIds: string[], dupError?: boolean } = {
                wbId: wbId,
                wbEntityIds: [],
                entityIds: []
            }
            _.forEach(results, (aRow) => {
                let rWbId = +aRow.WB_ID
                let rWbEntityId = +aRow.WB_ENTITY_ID
                let rEntityId = aRow.ENTITY_ID
                rv.wbEntityIds.push(rWbEntityId)
                rv.entityIds.push(rEntityId)

                if (rWbId != rv.wbId)
                    rv.dupError = true
            })
            return rv
        } else
            return {wbId: undefined, wbEntityIds: [], entityIds: []}

    }

    async createEntity(wbEntityId: number, key_set: string, id_ui: string, name: string, options?: IEntityCreateOptions): Promise<IEntity> {
        // Only support creation of PGs
        try {
            let e = await EntityTypeUtils.getEntityTypeRow(wbEntityId)
            let dbContext: PAdbContext = options?.dbContext
            if (_.isEmpty(dbContext))
                dbContext = new PAdbContext()

            let prdM = <WB_ProductTypeRow>await dbContext.find(esWB_Product_Type, e.cif.META_WB_ID)

            if (_.isEmpty(prdM))
                throw new Error("Failed Entity Create, unknown Product Type")

            if (prdM.cif.PRDM_TYPE != 'ProductGroup')
                throw new Error(`Failed Entity Create, not supported create type, ${prdM.cif.PRDM_ID}`)

            let entityCheck = await this.checkEntityExists(wbEntityId, key_set, id_ui)
            if (_.isUndefined(entityCheck))
                throw new Error("Failed Entity Check")

            if (entityCheck?.dupError)
                throw new Error(`Failed Entity Check - duplicate: ${key_set}, id: ${id_ui}`)

            if (entityCheck.wbId > 0)
                throw new Error(`Entity Exists - duplicate, key_set: ${key_set}, id: ${id_ui}.  WbId: ${entityCheck.wbId}`)


            if (e.cif.SET_TYPE != "ProductGroup")
                throw new Error(`Product Entity Manager doesn't support creating ${e.cif.ENTITY_NAME}, set type must be Product Group`)


            let oBO: ProductGroupBO = undefined
            oBO = new ProductGroupBO()

            await oBO.LoadNew({
                dbContext: dbContext,
                wbMasterDataSetId: e.cif.WB_MDSET_ID,
                sKeySetId: key_set,
                sId: id_ui,
                sName: name,
                wbPrdMId: e.cif.META_WB_ID,
                wbSalesAreaId: options.wbSalesAreaId || [],

            })
            //-- Support Legacy hierarchy code
            oBO.WB_ProductGroup.cif.LEVEL_ID = e.cif.ENTITY_ID
            await oBO.Save()

            // @ts-ignore
            return oBO.Entity

        } catch (err) {
            throw new Error(err)
        }
    }
}